"use client"

import { useTranslations } from "next-intl"
import React, { useState, useEffect, useCallback, useRef } from "react"
import { useMediaQuery } from "@/hooks/use-media-query"
import { useModalContainer } from "@/hooks/use-modal-container"
import { DialogCard } from "@/components/ui/dialog-card"
import { X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"
import { SHEET_PANEL_DARK, SHEET_Z_DEFAULT } from "@/lib/sheet-styles"
import type { TUser } from "@/types/User"
import type { ListingData } from "@/types/listing"

import { useToast } from "@/hooks/use-toast"
import { useListingDrafts } from "@/hooks/use-listing-drafts"
import { useOverlay } from "@/components/context/overlay-context"
import { ListingDraftError } from "@/lib/listings/listing-draft-api"
import {
  emptyListingData,
  isListingDraftMeaningful,
  listingDraftSignature,
} from "@/lib/listings/sell-draft"

import { CreationDraftsEntry } from "./creation-drafts-entry"
import { ListingCreationSteps } from "./listing-creation-steps"
import { QuitCreationDialog, type QuitFlowState } from "./quit-creation-dialog"
import { UniversalSuccessStep } from "./universal-success-step"
import { ProgressIndicator } from "./listing-creation/progress-indicator"

interface HeaderProgress {
  steps: string[]
  currentStep: number
  onStepClick: (index: number) => void
}

interface HeaderInfo {
  title?: string
  description?: string
  onBack?: () => void
  progress?: HeaderProgress
  tabs?: React.ReactNode
}

interface CreationFlowContainerProps {
  open: boolean
  onOpenChangeAction: (open: boolean) => void
  user: TUser
  initialScrollPosition?: number
  /**
   * Draft resume — the parsed draft's form state, resume step, and row id
   * travel together (global-creation-modal's ResumeDraftGate fetches BEFORE
   * mounting, so all three seed useState/useRef initializers).
   */
  initialData?: ListingData
  initialStep?: number
  draftId?: string
}

type CreationType = "listing" | null
type ViewState = "selection" | "listing" | "success"

/** Desktop panel geometry — the sheet twin here is the custom full-screen mobile flow above. */
const DESKTOP_PANEL_CLASS = "max-w-2xl h-[80dvh] max-h-[80dvh]"

export function CreationFlowContainer({
  open,
  onOpenChangeAction,
  user,
  initialScrollPosition = 0,
  initialData,
  initialStep,
  draftId,
}: CreationFlowContainerProps) {
  const t = useTranslations("creation");
  const { mounted } = useModalContainer() // 🎯 OPTIMIZATION: Use shared hook instead of duplicate state
  const isMobile = useMediaQuery("(max-width: 768px)")
  const mobileScrollContainerRef = useRef<HTMLDivElement>(null)
  
  const [viewState, setViewState] = useState<ViewState>("listing")
  const [creationType, setCreationType] = useState<CreationType>("listing")
  const [isCompleted, setIsCompleted] = useState(false)
  const [sheetHeaderContent, setSheetHeaderContent] = useState<HeaderInfo | null>(null)
  const [isFooterValid, setIsFooterValid] = useState(false)
  const [footerMeta, setFooterMeta] = useState<{ isLastStep: boolean; isSubmitting: boolean } | null>(null)
  const footerActionRef = useRef<(() => Promise<void>) | null>(null)

  // Données pour les différents flows — seeded from the resumed draft when
  // one is being reopened (ResumeDraftGate remounts on draftId change).
  const [listingData, setListingData] = useState<ListingData>(
    () => initialData ?? emptyListingData(),
  )

  // ---------------------------------------------------------------------
  // Quit guard — ONE chokepoint (`requestClose`) for every exit path:
  // desktop X/Escape/backdrop (DialogCard onOpenChange), mobile X
  // (`closeSheet`), back-chevron at step 0 (`handleBackToSelection`). A
  // meaningful, dirty form gets the save/discard dialog instead of dying.
  // ---------------------------------------------------------------------
  const { toast } = useToast()
  const tQuit = useTranslations("listingCreation.quitConfirm")
  // Drafts list feeds the landing's "Drafts" entry — fetched only while the
  // flow is open (the sell-hero mount keeps this container mounted closed).
  const {
    state: draftsState,
    saveDraft,
    discardDraft,
    deleteDraftRowOnly,
  } = useListingDrafts(open ? (user?.id ?? null) : null)
  const { openPanel } = useOverlay()
  const [quitFlow, setQuitFlow] = useState<QuitFlowState>({ kind: "idle" })

  // Latest-value refs: the guard predicates run inside stable event handlers
  // (beforeunload) and the dirty check must never read a stale closure.
  const listingDataRef = useRef(listingData)
  listingDataRef.current = listingData
  const currentStepRef = useRef(initialStep ?? 0)
  // Render-reactive twin of currentStepRef — the drafts entry only shows on
  // the landing step.
  const [activeStepIndex, setActiveStepIndex] = useState(initialStep ?? 0)
  // Which row a "Save as draft" upserts onto: the resumed draft, or the row
  // created by a previous save in this same session.
  const activeDraftIdRef = useRef<string | undefined>(draftId)

  // Baseline signature of the form the flow OPENED with (pristine for now;
  // a resumed draft seeds it in the resume milestone). A resumed-untouched
  // form and an auto-patched pristine form both close silently.
  const baselineSignatureRef = useRef<string | null>(null)
  if (baselineSignatureRef.current === null) {
    baselineSignatureRef.current = listingDraftSignature(listingData)
  }

  const isFormDirty = useCallback(() => {
    const data = listingDataRef.current
    return (
      isListingDraftMeaningful(data) &&
      listingDraftSignature(data) !== baselineSignatureRef.current
    )
  }, [])

  // Bumped on reset to remount ListingCreationSteps: the step machine's
  // internalStep (and every step's mirrored local state) must not survive a
  // reset on the always-mounted sell-hero instance.
  const [flowInstanceKey, setFlowInstanceKey] = useState(0)

  const resetFlow = useCallback(() => {
    setViewState("listing")
    setCreationType("listing")
    setIsCompleted(false)
    const empty = emptyListingData()
    setListingData(empty)
    baselineSignatureRef.current = listingDraftSignature(empty)
    currentStepRef.current = 0
    activeDraftIdRef.current = undefined
    setSheetHeaderContent(null)
    setFooterMeta(null)
    setIsFooterValid(false)
    footerActionRef.current = null
    setActiveStepIndex(0)
    setFlowInstanceKey((k) => k + 1)
  }, [])

  // Reset happens AFTER the panel is visually closed (see the !open effect
  // below) so the exit animation never flashes an emptied form.
  const pendingResetRef = useRef(false)
  const performClose = useCallback(() => {
    pendingResetRef.current = true
    setQuitFlow({ kind: "idle" })
    onOpenChangeAction(false)
  }, [onOpenChangeAction])

  const requestClose = useCallback(() => {
    if (quitFlow.kind !== "idle") return
    if (viewState === "success" || !isFormDirty()) {
      performClose()
      return
    }
    setQuitFlow({ kind: "confirming" })
  }, [quitFlow.kind, viewState, isFormDirty, performClose])

  const handleQuitCancel = useCallback(() => setQuitFlow({ kind: "idle" }), [])

  // Discards this SESSION's edits only — a previously saved draft row is
  // never deleted from here (that belongs to the Drafts section).
  const handleQuitDiscard = useCallback(() => performClose(), [performClose])

  const handleQuitSaveDraft = useCallback(async () => {
    setQuitFlow({ kind: "saving" })
    try {
      const saved = await saveDraft({
        data: listingDataRef.current,
        step: currentStepRef.current,
        draftId: activeDraftIdRef.current,
      })
      activeDraftIdRef.current = saved.draftId
      toast({ title: tQuit("savedToastTitle"), description: tQuit("savedToastDesc") })
      performClose()
    } catch (err) {
      console.error("[creation-flow] draft save failed", err)
      const limitReached =
        err instanceof ListingDraftError && err.code === "DRAFT_LIMIT_REACHED"
      setQuitFlow({
        kind: "saveError",
        message: limitReached ? tQuit("limitReached") : tQuit("saveError"),
      })
    }
  }, [saveDraft, toast, tQuit, performClose])

  // Native tab-close/refresh warning while a dirty form is open. The browser
  // shows its own generic prompt — no custom UI is possible here.
  useEffect(() => {
    if (!open || viewState === "success") return
    const handler = (e: BeforeUnloadEvent) => {
      if (!isFormDirty()) return
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [open, viewState, isFormDirty])

  const handleBackToSelection = useCallback(() => {
    requestClose()
  }, [requestClose])

  // Resume from the landing's drafts entry: close THIS flow instance (works
  // for both the panel mount and the always-mounted sell-hero one) and reopen
  // the creation panel routed through ResumeDraftGate. Only reachable while
  // the form is pristine (see renderInner's gate), so no data can be lost.
  const handleResumeDraft = useCallback(
    (resumeId: string) => {
      if (resumeId === activeDraftIdRef.current) return
      performClose()
      openPanel({ kind: "creation", draftId: resumeId })
    },
    [performClose, openPanel],
  )

  const handleComplete = useCallback(() => {
    setIsCompleted(true)
    setViewState("success")
    // Publish from a resumed/saved draft: ROW-ONLY cleanup, fire-and-forget —
    // the images now belong to the published listing (never the delete route
    // here), and a failed cleanup must not disturb the success screen.
    const publishedDraftId = activeDraftIdRef.current
    if (publishedDraftId) {
      activeDraftIdRef.current = undefined
      void deleteDraftRowOnly(publishedDraftId).catch((err) =>
        console.error("[creation-flow] post-publish draft cleanup failed", err),
      )
    }
  }, [deleteDraftRowOnly])

  const handleContinueBrowsing = useCallback(() => {
    performClose()
  }, [performClose])

  // Callbacks pour les mises à jour du header et footer
  const updateHeader = useCallback((headerInfo: HeaderInfo) => {
    setSheetHeaderContent(headerInfo)
  }, [])

  const updateFooterAction = useCallback((action: (() => Promise<void>) | null) => {
    footerActionRef.current = action
    if (!action) {
      setFooterMeta(null)
      setIsFooterValid(false)
    }
  }, [])
  const updateFooterValid = useCallback((isValid: boolean) => setIsFooterValid(isValid), [])
  const updateFooterMeta = useCallback((meta: { isLastStep: boolean; isSubmitting: boolean }) => setFooterMeta(meta), [])

  const closeSheet = useCallback(() => requestClose(), [requestClose])

  useEffect(() => {
    if (open && isMobile) {
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = ""
      }
    }
  }, [open, isMobile])

  useEffect(() => {
    if (!open && (viewState === "success" || pendingResetRef.current)) {
      pendingResetRef.current = false
      resetFlow()
    }
  }, [open, viewState, resetFlow])

  useEffect(() => {
    if (viewState === "success") {
      updateHeader({
        title: "",
        description: undefined,
      })
      if (isMobile) {
        mobileScrollContainerRef.current?.scrollTo({ top: 0 })
      }
    }
  }, [viewState, updateHeader, isMobile])

  const [isPhotosStepFullViewport, setIsPhotosStepFullViewport] = useState(false)

  const hasFooter = footerMeta !== null
  const footerButtonText = footerMeta?.isLastStep
    ? (footerMeta.isSubmitting ? t("continue") : t("publish"))
    : t("continue")

  function renderFooterButton() {
    return (
      <Button
        onClick={() => footerActionRef.current?.()}
        disabled={!isFooterValid || !!footerMeta?.isSubmitting}
        className="group w-full rounded-full h-14 text-sm shadow-md"
        size="default"
      >
        {footerMeta?.isLastStep && footerMeta.isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {footerButtonText}
          </>
        ) : (
          <>
            {footerButtonText}
            <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </>
        )}
      </Button>
    )
  }

  function renderInner() {
    if (viewState === "listing") {
      // `fullViewportStep` existed for the old photo EDITOR, which owned the whole
      // screen and shipped its own continue button. Its replacement,
      // `PhotosUploadStep`, is a plain picker with no continue of its own — it
      // drives the flow footer via `onValidationChange`, so hiding that footer
      // would strand the seller on the Photos step. No step is full-viewport now;
      // the prop and its state are kept only so the layout branches below stay
      // intact until someone removes them deliberately.
      // Landing-only drafts entry: pristine form on step 0. The currently
      // resumed draft is excluded — deleting/re-resuming the row whose files
      // the open form references would strand it.
      const showDraftsEntry = activeStepIndex === 0 && !isFormDirty()
      const entryDraftsState =
        showDraftsEntry && draftsState.kind === "ready" && activeDraftIdRef.current
          ? {
              kind: "ready" as const,
              drafts: draftsState.drafts.filter((d) => d.id !== activeDraftIdRef.current),
            }
          : draftsState

      return (
        <>
          {showDraftsEntry ? (
            <CreationDraftsEntry
              state={entryDraftsState}
              onResumeAction={handleResumeDraft}
              onDeleteAction={discardDraft}
            />
          ) : null}
          <ListingCreationSteps
            key={flowInstanceKey}
            data={listingData}
            onDataChangeAction={(patch) => setListingData((prev) => ({ ...prev, ...patch }))}
            onBackToSelectionAction={handleBackToSelection}
            onCompleteAction={handleComplete}
            isMobile={isMobile}
            onHeaderChange={updateHeader}
            onFooterActionChange={updateFooterAction}
            onFooterValidChange={updateFooterValid}
            onFooterMetaChange={updateFooterMeta}
            onStepNameChange={() => setIsPhotosStepFullViewport(false)}
            // Post-reset instances always start at 0 — the draft's resume step
            // only applies to the first mount of a resumed flow.
            initialStep={flowInstanceKey === 0 ? initialStep : undefined}
            onStepVisit={(step) => {
              currentStepRef.current = step
              setActiveStepIndex(step)
            }}
            fullViewportStep={isPhotosStepFullViewport}
            compact
          />
        </>
      )
    }

    // Page de succès
    if (viewState === "success") {
      return (
        <div className={cn(
          isMobile ? "h-full overflow-y-auto overscroll-contain scrollbar-hide pt-8" : "h-full"
        )}>
          <UniversalSuccessStep
            data={listingData as any}
            onResetAction={() => {
              setViewState("listing")
              setCreationType("listing")
              setIsCompleted(false)
            }}
            onContinueBrowsingAction={handleContinueBrowsing}
            type={creationType || "listing"}
            userId={user?.id ?? undefined}
          />
        </div>
      )
    }

    return null
  }

  if (!mounted) return null

  /* Desktop chrome inputs. `sheetHeaderContent` is replaced wholesale by each
     step (see `updateHeader`), so "" really is "this step wants no title" and
     not "title not set yet" — hence the two-variant branch at the bottom. */
  const desktopTitle = sheetHeaderContent?.title ?? t("title")
  const desktopContentClass = isPhotosStepFullViewport
    ? "flex flex-col px-6 pt-0 overflow-y-hidden"
    : "px-6 pt-0"
  const desktopBelowTitle =
    sheetHeaderContent?.progress || sheetHeaderContent?.description || sheetHeaderContent?.tabs ? (
      <>
        {sheetHeaderContent?.progress ? (
          <div className="min-w-0">
            <ProgressIndicator
              steps={sheetHeaderContent.progress.steps}
              currentStep={sheetHeaderContent.progress.currentStep}
              onStepClickAction={sheetHeaderContent.progress.onStepClick}
              compact
              hideProgressBar
            />
          </div>
        ) : null}
        {sheetHeaderContent?.description ? (
          <p className="min-w-0 break-words pt-2 text-sm text-foreground/60 line-clamp-2">
            {sheetHeaderContent.description}
          </p>
        ) : null}
        {sheetHeaderContent?.tabs ? <div className="pt-3">{sheetHeaderContent.tabs}</div> : null}
      </>
    ) : null

  const panel = isMobile ? (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          // No hand-written `dark` here: `SHEET_PANEL_DARK` (appended below)
          // already carries it, so a second copy was a no-op duplicate — and a
          // landmine. Step 2f removes `dark` from the panel constants; a private
          // copy on this container would have survived that removal and left the
          // whole creation flow (14 steps, full-screen on mobile) dark while
          // every other overlay went light. Removed here so the flip stays the
          // documented set of points and nothing outlives it silently.
          // @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §4.1
          className={cn("fixed inset-0 flex flex-col overflow-hidden md:hidden w-full max-w-[100vw]", SHEET_PANEL_DARK)}
          style={{ zIndex: SHEET_Z_DEFAULT }}
        >
          {/* Chrome rows below paint NO surface of their own: they are plain flex
              children of the panel, nothing scrolls under them, and the panel is
              already `bg-sheet` (`SHEET_PANEL_DARK`). A second `bg-*` here would
              be the panel colour spelled twice — and the copy that gets forgotten
              on the day the panel goes light. */}
          <div
            className="shrink-0 w-full"
            style={{ height: "env(safe-area-inset-top)", minHeight: 0 }}
            aria-hidden
          />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col w-full">
            {!isPhotosStepFullViewport && (
              <header className="relative z-10 flex shrink-0 flex-col min-w-0 overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2">
                  {(sheetHeaderContent?.title !== undefined || sheetHeaderContent?.onBack) ? (
                    <>
                      {sheetHeaderContent?.onBack ? (
                        <button
                          type="button"
                          onClick={sheetHeaderContent.onBack}
                          className="h-9 w-9 shrink-0 flex items-center justify-center rounded-full bg-muted hover:bg-sheet-handle active:bg-sheet-handle transition-colors"
                          aria-label="Back"
                        >
                          <ChevronLeft className="h-5 w-5 text-muted-foreground" strokeWidth={2.5} />
                        </button>
                      ) : (
                        <div className="w-9 shrink-0" aria-hidden />
                      )}
                      <h2 className="font-semibold text-base text-sheet-foreground flex-1 min-w-0 truncate text-center">
                        {sheetHeaderContent?.title ?? t("title")}
                      </h2>
                      <button
                        type="button"
                        onClick={closeSheet}
                        className="h-9 w-9 shrink-0 flex items-center justify-center rounded-full bg-muted hover:bg-sheet-handle active:bg-sheet-handle transition-colors"
                        aria-label="Close"
                      >
                        <X className="h-5 w-5 text-muted-foreground" strokeWidth={2.5} />
                      </button>
                    </>
                  ) : (
                    <>
                      <h2 className="font-semibold text-base text-sheet-foreground flex-1 min-w-0 truncate">
                        {viewState === "success" ? "" : t("title")}
                      </h2>
                      <button
                        type="button"
                        onClick={closeSheet}
                        className="h-9 w-9 shrink-0 flex items-center justify-center rounded-full bg-muted hover:bg-sheet-handle active:bg-sheet-handle transition-colors"
                        aria-label="Close"
                      >
                        <X className="h-5 w-5 text-muted-foreground" strokeWidth={2.5} />
                      </button>
                    </>
                  )}
                </div>
                {sheetHeaderContent?.progress ? (
                  <div className="min-w-0 px-3 pb-3">
                    <ProgressIndicator
                      steps={sheetHeaderContent.progress.steps}
                      currentStep={sheetHeaderContent.progress.currentStep}
                      onStepClickAction={sheetHeaderContent.progress.onStepClick}
                      compact
                      hideProgressBar
                    />
                  </div>
                ) : null}
                {sheetHeaderContent?.description ? (
                  <p className="px-3 pb-4 text-sm text-foreground/60 line-clamp-2 break-words min-w-0">
                    {sheetHeaderContent.description}
                  </p>
                ) : null}
              </header>
            )}
            {!isPhotosStepFullViewport && sheetHeaderContent?.tabs ? (
              <div className="relative z-10 shrink-0 mb-3 px-3">{sheetHeaderContent.tabs}</div>
            ) : null}
            <div
              ref={mobileScrollContainerRef}
              className={
                isPhotosStepFullViewport
                  ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                  : "flex-1 min-h-0 min-w-0 w-full overflow-y-auto overflow-x-hidden px-3 pb-4 scrollbar-none [&::-webkit-scrollbar]:hidden"
              }
              style={
                isPhotosStepFullViewport
                  ? undefined
                  : viewState === "success"
                    ? { paddingBottom: "env(safe-area-inset-bottom)" }
                    : {
                        paddingBottom: hasFooter
                          ? "calc(6rem + env(safe-area-inset-bottom))"
                          : "5rem",
                      }
              }
            >
              {renderInner()}
            </div>
            {!isPhotosStepFullViewport && viewState === "listing" && hasFooter ? (
              <>
                <div
                  className="absolute bottom-0 left-0 right-0 z-10 h-20 pointer-events-none bg-linear-to-t from-sheet to-transparent"
                  aria-hidden
                />
                <div
                  className="absolute bottom-0 left-0 right-0 z-20 min-w-0 px-3 pt-4 text-sheet-foreground"
                  style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {renderFooterButton()}
                </div>
              </>
            ) : null}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  ) : desktopTitle === "" ? (
    /* The success screen deliberately paints no title (`updateHeader({ title: "" })`) —
       that is the `closeOnly` variant, NOT `standard` with an empty string. The same
       update clears `onBack`, `progress` and `tabs`, so the swap loses nothing. */
    <DialogCard
      open={open}
      onOpenChange={(next) => {
        if (!next) requestClose()
      }}
      chrome="closeOnly"
      a11yTitle={t("title")}
      panelClassName={DESKTOP_PANEL_CLASS}
      contentClassName={desktopContentClass}
      footer={hasFooter ? renderFooterButton() : undefined}
    >
      {renderInner()}
    </DialogCard>
  ) : (
    <DialogCard
      open={open}
      onOpenChange={(next) => {
        if (!next) requestClose()
      }}
      title={desktopTitle}
      onBack={sheetHeaderContent?.onBack}
      belowTitle={desktopBelowTitle}
      panelClassName={DESKTOP_PANEL_CLASS}
      contentClassName={desktopContentClass}
      footer={hasFooter ? renderFooterButton() : undefined}
    >
      {renderInner()}
    </DialogCard>
  )

  return createPortal(
    <>
      {panel}
      <QuitCreationDialog
        state={quitFlow}
        onCancelAction={handleQuitCancel}
        onDiscardAction={handleQuitDiscard}
        onSaveDraftAction={() => {
          void handleQuitSaveDraft()
        }}
      />
    </>,
    document.body,
  )
}
